import { NavLink } from 'react-router-dom';
import { clsx } from 'clsx';
import type { ComponentType } from 'react';
import { X } from 'lucide-react';
import {
  DashboardIcon,
  AdminUsersIcon,
  LendersIcon,
  ProvidersIcon,
  ActivityLogIcon,
  PipelineIcon,
  type SidebarIconProps,
} from './SidebarIcons';

/** An entry without `to` is a section the console does not have a screen for yet. */
type NavItem = { name: string; icon: ComponentType<SidebarIconProps>; to?: string; end?: boolean };

const navigation: NavItem[] = [
  // `end`, or the root would match — and highlight — on every page.
  { name: 'Dashboard', to: '/', icon: DashboardIcon, end: true },
  { name: 'Admin Users', icon: AdminUsersIcon },
  { name: 'Lenders', to: '/lenders', icon: LendersIcon },
  { name: 'Providers', icon: ProvidersIcon },
  { name: 'Activity Log', to: '/activity-log', icon: ActivityLogIcon },
  { name: 'Pipeline', icon: PipelineIcon },
];

const UNAVAILABLE_HINT = 'Not available yet';

interface SidebarProps {
  open: boolean;
  onClose: () => void;
  collapsed: boolean;
}

export default function Sidebar({ open, onClose, collapsed }: SidebarProps) {
  return (
    <>
      {/* Mobile overlay */}
      {open && (
        <div
          className="fixed inset-0 z-40 bg-black/30 lg:hidden"
          onClick={onClose}
        />
      )}

      <aside
        className={clsx(
          'app-sidebar fixed inset-y-0 left-0 z-50 flex flex-col transition-all duration-200 lg:static lg:z-auto',
          collapsed ? 'lg:w-16' : 'lg:w-[270px]',
          // Mobile: always full width, slide in/out
          open ? 'translate-x-0 w-[270px]' : '-translate-x-full w-[270px]',
          'lg:translate-x-0',
        )}
      >
        {/* Only the drawer's close control: the logo lives in the header. */}
        <div className="flex h-12 items-center justify-end px-2 lg:hidden">
          <button onClick={onClose} className="inline-flex items-center justify-center rounded p-2 text-white hover:bg-white/10" aria-label="Close navigation">
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Navigation */}
        <nav className={clsx('flex-1 overflow-y-auto py-2', collapsed ? 'px-1.5' : 'px-2')}>
          <div className="space-y-0.5">
            {navigation.map((item) => {
              const ItemIcon = item.icon;
              const layout = collapsed ? 'justify-center p-2' : 'gap-2.5 px-3 py-2';
              // 15px/600 white, per the design system's sidebar.
              const base = 'flex items-center rounded text-base font-semibold transition-colors';

              if (!item.to) {
                return (
                  <button
                    key={item.name}
                    type="button"
                    disabled
                    title={collapsed ? `${item.name} — ${UNAVAILABLE_HINT}` : UNAVAILABLE_HINT}
                    className={clsx(base, layout, 'w-full cursor-not-allowed text-white/50')}
                  >
                    <ItemIcon className="h-5 w-5 shrink-0" />
                    {!collapsed && <span className="truncate">{item.name}</span>}
                  </button>
                );
              }

              return (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.end}
                  onClick={onClose}
                  title={collapsed ? item.name : undefined}
                  className={({ isActive }) =>
                    clsx(base, layout, 'text-white', isActive ? 'bg-white/20' : 'hover:bg-white/10')
                  }
                >
                  <ItemIcon className="h-5 w-5 shrink-0" />
                  {!collapsed && <span className="truncate">{item.name}</span>}
                </NavLink>
              );
            })}
          </div>
        </nav>

      </aside>
    </>
  );
}
